import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import LegalShell, { Section, P, UL, Notice } from '../components/LegalShell';
import { BRAND_NAME } from '../utils/brand';

//
// /safety — Safety Information page.
// Source: Website Build Handoff V1.0, Appendix B §20 (Safety) + §21 (Side
// Effects) + spec §9 (Compliance Guardrails) + relevant FAQ items.
//
// Compliance notes:
//   • Must NOT promise a guarantee of safety, prescription, or weight loss.
//   • Side-effect list is intentionally not exhaustive — must be reviewed
//     against actual medication labels / partner pharmacist before launch.
//   • Emergency disclaimer must remain visible.
//

export default function Safety() {
  useEffect(() => {
    const prev = document.title;
    document.title = `Safety Information | ${BRAND_NAME}`;
    return () => { document.title = prev; };
  }, []);

  return (
    <LegalShell
      eyebrow="Patient Safety"
      title="Safety Information"
      intro={
        `Important information about medical weight loss treatment, GLP-1-based ` +
        `care, side effects, urgent warning signs and when to seek medical help. ` +
        `Read carefully and discuss anything that applies to you with a licensed ` +
        `healthcare provider.`
      }
      version="1.0 (Draft — pending medical, pharmacist and legal review)"
      lastUpdated="June 2026"
      governingLaw="Malaysia"
    >
      <Section id="overview" number={1} heading="Your safety comes first">
        <Notice tone="warning">
          <strong>Emergency notice.</strong> {BRAND_NAME} does not provide
          emergency medical services. If you are experiencing a medical
          emergency or serious symptoms, call your local emergency number or go
          to the nearest hospital immediately.
        </Notice>
        <P>
          Medical weight loss treatments can have side effects and are not
          suitable for everyone. Before starting any medication, your doctor
          should explain the expected benefits, possible risks, warning signs,
          alternatives and follow-up requirements.
        </P>
        <P>
          You should provide accurate information about your medical history,
          current medication, supplements, allergies, pregnancy plans, previous
          reactions and upcoming procedures. Incomplete or inaccurate
          information can affect the safety of any treatment.
        </P>
      </Section>

      <Section id="before-you-start" number={2} heading="Before you start treatment">
        <P>
          Treatment decisions should only be made after a consultation with a
          licensed healthcare provider. Make sure your doctor knows about the
          following:
        </P>
        <UL>
          <li>Current and past medical conditions</li>
          <li>All prescription medication, over-the-counter products and supplements you take</li>
          <li>Allergies and previous reactions to medications</li>
          <li>Pregnancy, breastfeeding or pregnancy planning status</li>
          <li>Previous experience with weight-loss medication (including GLP-1)</li>
          <li>Personal or family history of medullary thyroid carcinoma or multiple endocrine neoplasia syndrome type 2 (MEN 2)</li>
          <li>History of pancreatitis, gallbladder disease, kidney disease, severe gastrointestinal disease or diabetic retinopathy</li>
          <li>Eating disorder history</li>
          <li>Any upcoming surgery, endoscopy or procedure requiring anaesthesia or deep sedation</li>
        </UL>
      </Section>

      <Section id="how-to-use" number={3} heading="Using prescription medication safely">
        <UL>
          <li>Always follow your doctor's instructions and the directions on your prescription label.</li>
          <li>Do not change the dose or stop treatment without speaking to your doctor first.</li>
          <li>Do not share prescription medication with anyone — including family members.</li>
          <li>Do not buy prescription medication from unverified online sellers, social media, or any source that is not a licensed pharmacy. Counterfeit, expired or unregistered products can be dangerous.</li>
          <li>Store medication according to the label instructions (some require refrigeration).</li>
          <li>Attend follow-up appointments and complete recommended check-ins.</li>
        </UL>
      </Section>

      <Section id="possible-side-effects" number={4} heading="Possible side effects">
        <P>
          Some GLP-1-based treatments may cause gastrointestinal symptoms,
          especially during dose changes. Your doctor or pharmacist can guide
          you on what to expect, what can be managed at home, and what
          requires medical attention.
        </P>
        <P><strong>Commonly reported symptoms include:</strong></P>
        <UL>
          <li>Nausea</li>
          <li>Vomiting</li>
          <li>Diarrhoea</li>
          <li>Constipation</li>
          <li>Bloating or stomach discomfort</li>
          <li>Reduced appetite</li>
          <li>Reflux or indigestion</li>
        </UL>
        <Notice tone="info">
          This list is intentionally <strong>not exhaustive</strong>. Exact
          risks depend on the medication prescribed and your individual health
          profile. Your prescribing doctor and pharmacist must explain the
          specific risks of the medicine they recommend, including any
          contraindications and warnings that apply to you.
        </Notice>
      </Section>

      <Section id="urgent" number={5} heading="When to seek urgent medical help">
        <Notice tone="warning">
          Seek immediate medical attention if you experience any of the
          following while undergoing treatment or assessment:
        </Notice>
        <UL>
          <li>Severe or persistent abdominal pain (especially pain that radiates to your back)</li>
          <li>Repeated vomiting or inability to keep fluids down</li>
          <li>Signs of an allergic reaction — rash, facial or throat swelling, difficulty breathing</li>
          <li>Chest pain, fainting, severe dizziness</li>
          <li>Yellowing of skin or eyes (jaundice)</li>
          <li>Severe dehydration, confusion or unusual drowsiness</li>
          <li>Any symptom that feels serious or unusual to you</li>
        </UL>
        <P>
          Do not wait for your next scheduled review. Call your local emergency
          number, go to the nearest hospital, or contact your doctor
          immediately.
        </P>
      </Section>

      <Section id="who-may-not" number={6} heading="Who may not be suitable">
        <P>
          Medical weight loss treatment is not suitable for everyone. A doctor
          will need to understand your full medical history before deciding
          whether any prescription treatment is appropriate. The following may
          require special assessment or alternative care:
        </P>
        <UL>
          <li>Pregnant individuals or those planning pregnancy</li>
          <li>Breastfeeding individuals</li>
          <li>People with a history of certain thyroid cancers or endocrine conditions</li>
          <li>People with a history of pancreatitis, severe gastrointestinal disease, kidney problems, gallbladder disease, diabetic retinopathy or eating disorders</li>
          <li>People with complex medical conditions or multiple medications</li>
          <li>People scheduled for surgery, endoscopy or procedures requiring anaesthesia / deep sedation</li>
        </UL>
        <P>
          This is not a complete contraindication list. Final decisions must be
          made by a licensed healthcare provider after reviewing your full
          medical context.
        </P>
      </Section>

      <Section id="surgery" number={7} heading="Surgery, endoscopy and anaesthesia">
        <P>
          If you are using or considering any GLP-1 medication, tell your
          doctor, surgeon or anaesthetist before any planned procedure. Delayed
          stomach emptying associated with some GLP-1 medications can affect
          procedures that involve anaesthesia or deep sedation. Your healthcare
          team may give specific instructions about timing and fasting.
        </P>
      </Section>

      <Section id="pregnancy" number={8} heading="Pregnancy, breastfeeding and family planning">
        <P>
          Certain weight-loss medications are not recommended during pregnancy
          or breastfeeding, and some may need to be stopped before attempting
          pregnancy. If you are pregnant, breastfeeding, or planning a
          pregnancy, speak to your doctor before considering any medication.
        </P>
      </Section>

      <Section id="ai-boundary" number={9} heading="The role of AI on the platform">
        <P>
          {BRAND_NAME} uses AI to support intake, education, reminders and
          progress tracking. The AI assistant is <strong>not</strong> a doctor.
          It does not diagnose medical conditions, prescribe medication,
          replace emergency care, or override a licensed healthcare provider's
          judgement.
        </P>
        <P>
          The AI-assisted assessment is a screening tool only. The result you
          see (such as "may be suitable for doctor review" or "speak to a
          doctor first") is a triage prompt — not a clinical decision.
        </P>
      </Section>

      <Section id="reporting" number={10} heading="Reporting side effects or safety concerns">
        <P>
          If you experience a side effect or safety concern related to
          medication you have been prescribed, contact your prescribing doctor
          or the dispensing pharmacist as soon as practical. For urgent
          symptoms, seek emergency care.
        </P>
        <P>
          You may also notify {BRAND_NAME} so we can support care coordination.
          You can reach our team via the{' '}
          <Link to="/contact" className="text-brand-700 underline hover:no-underline">
            Contact page
          </Link>
          {' '}— but this is not a substitute for direct medical care.
        </P>
      </Section>

      <Section id="data" number={11} heading="How your health information is handled">
        <P>
          Information you share through the platform is handled according to
          our{' '}
          <Link to="/privacy" className="text-brand-700 underline hover:no-underline">
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link to="/sensitive-data-consent" className="text-brand-700 underline hover:no-underline">
            Sensitive Personal Data Consent
          </Link>. Sensitive health information is protected and used only for
          relevant healthcare and platform purposes, including review by
          licensed healthcare providers and pharmacy partners where you
          consent.
        </P>
      </Section>

      <Section id="disclaimer" number={12} heading="Medical disclaimer">
        <P>
          {BRAND_NAME} is a healthcare technology and care coordination
          platform operated by Melonco Sdn. Bhd. {BRAND_NAME} facilitates
          access to healthcare services provided by independent licensed
          healthcare providers and pharmacy partners. {BRAND_NAME} does not
          itself practise medicine, diagnose medical conditions, prescribe
          medication or dispense medication. Prescription medication is
          supplied only where legally prescribed and medically appropriate.
          Results vary between individuals and cannot be guaranteed.
        </P>
      </Section>
    </LegalShell>
  );
}
